import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Skeleton } from '@/components/ui/skeleton';
import { getBilling, updateBilling } from '@/lib/profileApi';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';

const BillingTab = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const [form, setForm] = useState({
    billing_address_line1: '',
    billing_address_line2: '',
    billing_city: '',
    billing_county: '',
    billing_postcode: '',
    billing_country: 'GB',
    invoice_mode: 'per_job',
    billing_email: '',
    vat_number: '',
    default_po_number: '',
  });
  const [initial, setInitial] = useState(null);

  const load = useCallback(async () => {
    try {
      const data = await getBilling();
      const loaded = {
        billing_address_line1: data.billing_address_line1 || '',
        billing_address_line2: data.billing_address_line2 || '',
        billing_city: data.billing_city || '',
        billing_county: data.billing_county || '',
        billing_postcode: data.billing_postcode || '',
        billing_country: data.billing_country || 'GB',
        invoice_mode: data.invoice_mode || 'per_job',
        billing_email: data.billing_email || '',
        vat_number: data.vat_number || '',
        default_po_number: data.default_po_number || '',
      };
      setForm(loaded);
      setInitial(loaded);
    } catch (err) {
      toast.error(err.message || 'Failed to load billing info');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!initial) return;
    setDirty(Object.keys(form).some(k => form[k] !== initial[k]));
  }, [form, initial]);

  const setField = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  const handleSave = async () => {
    if (!dirty) return;
    setSaving(true);
    try {
      await updateBilling(form);
      setInitial({ ...form });
      setDirty(false);
      toast.success('Billing preferences saved');
    } catch (err) {
      toast.error(err.message || 'Failed to save billing info');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3, 4, 5].map(i => (<div key={i} className="space-y-2"><Skeleton className="w-24 h-4" /><Skeleton className="w-full max-w-md h-10" /></div>))}
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Billing Address */}
      <div>
        <h3 className="text-lg font-medium mb-4">Billing Address</h3>
        <p className="text-sm text-muted-foreground mb-4">This address will appear on your invoices.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl">
          <div className="md:col-span-2">
            <Label htmlFor="billing-line1">Address Line 1</Label>
            <Input id="billing-line1" value={form.billing_address_line1} onChange={e => setField('billing_address_line1', e.target.value)} placeholder="123 High Street" className="mt-2" />
          </div>
          <div className="md:col-span-2">
            <Label htmlFor="billing-line2">Address Line 2</Label>
            <Input id="billing-line2" value={form.billing_address_line2} onChange={e => setField('billing_address_line2', e.target.value)} placeholder="Suite 4, Floor 2" className="mt-2" />
          </div>
          <div>
            <Label htmlFor="billing-city">City</Label>
            <Input id="billing-city" value={form.billing_city} onChange={e => setField('billing_city', e.target.value)} placeholder="London" className="mt-2" />
          </div>
          <div>
            <Label htmlFor="billing-county">County</Label>
            <Input id="billing-county" value={form.billing_county} onChange={e => setField('billing_county', e.target.value)} placeholder="Greater London" className="mt-2" />
          </div>
          <div>
            <Label htmlFor="billing-postcode">Postcode</Label>
            <Input id="billing-postcode" value={form.billing_postcode} onChange={e => setField('billing_postcode', e.target.value)} placeholder="EC2A 1NT" className="mt-2" />
          </div>
          <div>
            <Label htmlFor="billing-country">Country</Label>
            <Input id="billing-country" value={form.billing_country} onChange={e => setField('billing_country', e.target.value)} placeholder="GB" className="mt-2" />
          </div>
        </div>
      </div>

      <hr className="border-border" />

      {/* Invoice details */}
      <div>
        <h3 className="text-lg font-medium mb-4">Invoice Details</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl">
          <div className="md:col-span-2">
            <Label htmlFor="billing-email">Send invoices to</Label>
            <Input id="billing-email" type="email" value={form.billing_email} onChange={e => setField('billing_email', e.target.value)} placeholder="accounts@yourcompany.com" className="mt-2" />
            <p className="text-xs text-muted-foreground mt-1">Leave blank to use your sign-in email.</p>
          </div>
          <div>
            <Label htmlFor="billing-vat">VAT number</Label>
            <Input id="billing-vat" value={form.vat_number} onChange={e => setField('vat_number', e.target.value)} placeholder="GB123456789" className="mt-2" />
          </div>
          <div>
            <Label htmlFor="billing-po">Default PO reference</Label>
            <Input id="billing-po" value={form.default_po_number} onChange={e => setField('default_po_number', e.target.value)} className="mt-2" />
            <p className="text-xs text-muted-foreground mt-1">Added to new orders.</p>
          </div>
        </div>
      </div>

      <hr className="border-border" />

      {/* Invoice Preference */}
      <div>
        <h3 className="text-lg font-medium mb-2">Invoice Preference</h3>
        <p className="text-sm text-muted-foreground mb-4">Choose how you'd like to receive invoices for your job orders.</p>
        <RadioGroup value={form.invoice_mode} onValueChange={v => setField('invoice_mode', v)} className="space-y-3 max-w-xl">
          <label className={`flex items-start gap-3 p-4 border rounded-lg cursor-pointer transition-colors ${form.invoice_mode === 'per_job' ? 'border-primary bg-primary/5' : 'border-border hover:border-muted-foreground/30'}`}>
            <RadioGroupItem value="per_job" className="mt-0.5" />
            <div>
              <p className="text-sm font-medium">Individual invoice per job order</p>
              <p className="text-xs text-muted-foreground mt-1">Receive a separate invoice for each job you submit. Best for companies filing individually.</p>
            </div>
          </label>
          <label className={`flex items-start gap-3 p-4 border rounded-lg cursor-pointer transition-colors ${form.invoice_mode === 'consolidated' ? 'border-primary bg-primary/5' : 'border-border hover:border-muted-foreground/30'}`}>
            <RadioGroupItem value="consolidated" className="mt-0.5" />
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <p className="text-sm font-medium">Consolidated invoice per order cycle</p>
                <span className="px-2 py-0.5 text-xs font-medium bg-muted rounded-full text-muted-foreground">Recommended for firms</span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">Receive one combined invoice for all companies in a single order, listing each company's Job ID and services. UK orders only. Ideal for accounting firms managing multiple clients.</p>
            </div>
          </label>
        </RadioGroup>
      </div>

      {/* Save */}
      <div className="flex items-center gap-3 pt-2">
        <Button onClick={handleSave} disabled={!dirty || saving}>
          {saving ? 'Saving\u2026' : 'Save Billing Preferences'}
        </Button>
        {dirty && <p className="text-xs text-muted-foreground">You have unsaved changes</p>}
      </div>
    </div>
  );
};

export default BillingTab;
